<template>
  <div class="webassistant-preview-container">
    <div class="webassistant-preview-root">
      <div class="wa-container">
        <div class="wa-chatbox">
          <transition name="fade-shadow">
            <div class="wa-shadow" v-show="isOpen"></div>
          </transition>
          <transition name="fade">
            <div
              class="chat-container notFullscreen"
              :class="{ 'chat-container__static': disableInteraction }"
              v-show="isOpen"
            >
              <HomeScreenPreview
                v-show="activeView === 'homescreen'"
                :brandColour="brandColour"
                :actionColour="actionColour"
                :homescreenImageUrl="homescreenImageUrl"
                :greetingText="greetingText"
                :introductionText="introductionText"
                :typical-response-time="typicalResponseTimeString"
                :homescreenApps="homescreenApps"
              >
              </HomeScreenPreview>

              <ConversationPreview
                v-show="activeView === 'conversation'"
                :brand-colour="brandColour"
                :outboundMessageColour="outboundMessageColour"
                :action-colour="actionColour"
                :header-text="headerText"
                :messages="messages"
                :typical-response-time="typicalResponseTimeString"
                :readonly-apps="readonlyApps"
                @sendQuickReply="(quickReply) => emit('sendQuickReply', quickReply)"
                @messageSend="(message) => emit('messageSend', message)"
                @conversationRead="emit('conversationRead')"
                @typingStart="emit('typingStart')"
                @typingStop="emit('typingStop')"
              >
              </ConversationPreview>
            </div>
          </transition>
        </div>
        <WebAssistantLauncher
          v-if="displayLauncher"
          :backgroundColour="brandColour"
          :iconColour="getContrastingBrandColour()"
          :isOpen="isOpen"
          @buttonClick="$emit('launcherButtonClick')"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import Vue, { computed, PropType } from 'vue';
import HomeScreenPreview from './homescreenPreview/HomeScreenPreview.vue';
import ConversationPreview from './conversationPreview/ConversationPreview.vue';
import WebAssistantLauncher from './WebAssistantLauncher.vue';
import ColourService from '@/shared/services/color';
import { HomescreenAppInitViewModel, TypicalResponseTimeOptions } from '../models';
import { ConversationMessage } from '@/features/webassistant/models/conversation-message';

const props = defineProps({
  brandColour: {
    type: String,
  },
  outboundMessageColour: {
    type: String,
  },
  actionColour: {
    type: String,
  },
  headerText: {
    type: String,
  },
  homescreenImageUrl: {
    type: String,
  },
  greetingText: {
    type: String,
  },
  introductionText: {
    type: String,
  },
  typicalResponseTime: {
    type: Number,
    required: true,
  },
  displayLauncher: {
    type: Boolean,
    default: true,
  },
  messages: {
    type: Array as PropType<ConversationMessage[]>,
  },
  isOpen: {
    type: Boolean,
    required: true,
  },
  activeView: {
    type: String as PropType<'homescreen' | 'conversation'>,
    default: 'homescreen',
  },
  disableInteraction: {
    type: Boolean,
  },
  readonlyApps: {
    type: Boolean,
    default: true,
  },
  homescreenApps: {
    type: Array as PropType<HomescreenAppInitViewModel[]>,
  },
});

const emit = defineEmits<{
  (e: 'load', value: any): void;
  (e: 'sendQuickReply', value: any): void;
  (e: 'messageSend', value: any): void;
  (e: 'conversationRead'): void;
  (e: 'typingStart'): void;
  (e: 'typingStop'): void;
  (e: 'launcherButtonClick'): void;
}>();

const typicalResponseTimeString = computed(() => {
  if (props.typicalResponseTime === 0) {
    return 'Web Assistant';
  }
  return `We typically reply ${TypicalResponseTimeOptions[props.typicalResponseTime].label.toLowerCase()}`;
});

function getContrastingBrandColour() {
  return ColourService.getContrastingColour(props.brandColour);
}
</script>

<style lang="scss">
.webassistant-preview-root {
  font-family: 'Open Sans', Helvetica, sans-serif;
  font-size: 0.8rem;
  margin-top: auto;
  height: 100%;

  .title,
  .subtitle {
    color: unset;
  }

  button {
    font-family: Arial;
  }

  .chat-container {
    position: relative; // makes 'position: absolute' relative to this component
  }
}
</style>

<style lang="scss" scoped>
.webassistant-preview-container {
  // for aligning the toggle buttons with the preview
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
}
// replaces hidden element behind the widget that provides the shadow
.chat-container {
  &__static {
    .conversation-container {
      pointer-events: none;
    }
  }
}

.wa-container {
  position: relative;
  flex-direction: column;
  align-items: flex-end;
  height: 100%;
}
.chat-container {
  height: 100%;
  width: 100%;
  display: flex;
  flex-flow: column;
  background: white;

  &__static {
    height: 539px;
  }
}
.notFullscreen {
  border-radius: 8px;
  width: 380px;
}
.wa-chatbox {
  position: relative;
  height: calc(100% - 74px);
}
.wa-shadow {
  position: absolute;
  top: 1rem;
  left: 0.5rem;
  height: calc(100% - 2rem);
  width: calc(100% - 1rem);
  box-shadow: rgb(0 0 0 / 15%) 0px 0px 20px 20px;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-shadow-enter-active {
  transition: opacity 0.2s 0.02s;
}
.fade-enter,
.fade-leave-to,
.fade-shadow-enter,
.fade-shadow-leave-to {
  opacity: 0;
}
</style>
